import {Api as GenericApi} from 'common/types/api/generic/api'
import {Instance, flow, types as t, SnapshotIn, getParent} from 'mobx-state-tree'
import {injectFromEnv} from '../injectors'
import {isEqual, pickBy} from 'lodash-es'
import moment, {Moment} from 'moment'
import Store from '../Store'

type UrlStateParams = {
  region?: string
  zone?: string
  from?: string
  to?: string
}
type StateSnapshotIn = SnapshotIn<typeof State>

export type EnhancedZoneDataAttributes = {
  attributes: {
    zoneDetails: Array<{
      attributes: {
        zoneData: Array<{
          attributes: {
            freeCouriersCount?: number
            activeCouriersCount?: number
          }
        }>
      }
    }>
  }
}

export type Report = GenericApi['/shift-totals-report']['GET']['response']['data'] & EnhancedZoneDataAttributes

const TIME_URL_FORMAT = 'YYYY-MM-DDTHH:mm:ss'
const TIME_API_FORMAT = 'DD.MM.YYYY HH:mm'
export const DEFAULT_REGION = ''

const parseNumberUrlParam = (param?: string) =>
  param && !isNaN(Number(param)) ? param : undefined

const State = t.model({
  region: t.optional(t.string, DEFAULT_REGION),
  zone: t.maybeNull(t.string),
  from: t.optional(t.number, () => moment().startOf('day').milliseconds(0).valueOf()),
  to: t.optional(t.number, () => moment().endOf('day').milliseconds(0).valueOf())
})

const CttShiftsTotalReportPage = injectFromEnv('api')
.named('CttShiftsTotalReportPage')
.props({
  state: t.maybeNull(State),
  pending: t.optional(t.boolean, false),
  report: t.maybeNull(t.frozen<Report>())
})
.views((self) => {
  const views = {
    get pageUrlParams () {
      return self.state ? pickBy({
        region: self.state.region,
        zone: self.state.zone,
        from: moment(self.state.from).format(TIME_URL_FORMAT),
        to: moment(self.state.to).format(TIME_URL_FORMAT)
      }) : {}
    }
  }

  return views
})
.actions((self) => {
  const fetchReport = flow(function * () {
    self.pending = true
    const state = self.state
    try {
      const response: GenericApi['/shift-totals-report']['GET']['response'] = (
        yield self.$env.api.ctt.get('/shift-totals-report', {
          params: {
            filters: {
              region: +state.region,
              zones: state.zone ? [+state.zone] : undefined,
              from: moment(state.from).format(TIME_API_FORMAT),
              to: moment(state.to).format(TIME_API_FORMAT)
            }
          }
        })
      ).data

      const zones = response.data.attributes.zoneDetails.map((zone) => zone.attributes.zoneId)

      if (!zones.length) {
        self.report = response.data as Report

        return undefined
      }

      const responseZonesWorkload: GenericApi['/delivery-zone-workload']['GET']['response'] = (
        yield self.$env.api.ctt.get('/delivery-zone-workload', {
          params: {
            filters: {
              zones,
              from: moment(state.from).format(TIME_API_FORMAT),
              to: moment(state.to).format(TIME_API_FORMAT)
            }
          }
        })
      ).data

      response.data.attributes.zoneDetails = response.data.attributes.zoneDetails.map(
        (zone) => {
          const response = zone
          const zoneId = zone.attributes.zoneId
          response.attributes.zoneData = zone.attributes.zoneData.map((zoneData) => {
            const zoneDataFrom = zoneData.attributes.timeInterval.attributes.from
            const workload = responseZonesWorkload.data.find((zoneWorkload) =>
              (zoneWorkload.attributes.zoneId === zoneId) && zoneDataFrom === zoneWorkload.attributes.from)

            if (!workload) {
              return zoneData
            }
            const response: any = zoneData

            response.attributes.freeCouriersCount = workload.attributes.freeCouriersCount
            response.attributes.activeCouriersCount = workload.attributes.activeCouriersCount

            return response
          })

          return response
        }
      )

      self.report = response.data as Report
    } finally {
      self.pending = false
    }
  })

  const initialize = flow(function * (state: StateSnapshotIn = {}) {
    const newState = State.create(state)

    if (self.state && isEqual(newState, self.state)) {
      return false
    }

    self.state = newState
    yield fetchReport()

    return true
  })

  const initializeFromUrlParams = flow(function * (urlStateParams: UrlStateParams) {
    let from: Moment | undefined = moment(urlStateParams.from, TIME_URL_FORMAT)
    let to: Moment | undefined  = moment(urlStateParams.to, TIME_URL_FORMAT)
    const zone = parseNumberUrlParam(urlStateParams.zone)
    const region = parseNumberUrlParam(urlStateParams.region)

    if (!from.isValid() || !to.isValid() || moment(from).add(1, 'day').isBefore(to)) {
      from = undefined
      to = undefined
    }

    yield (getParent<typeof Store>(self)).loadRegionZones(region)

    return yield initialize({
      zone,
      region,
      from: from && from.valueOf(),
      to: to && to.valueOf()
    })
  })

  const resetForm = () => {
    self.state = null
    // tslint:disable:no-console
    initialize().catch((e) => console.warn(e))
  }

  return {
    initialize,
    initializeFromUrlParams,
    fetchReport,
    resetForm
  }
})

export default CttShiftsTotalReportPage
export type CttShiftsTotalReportPageType = Instance<typeof CttShiftsTotalReportPage>
